// v3.15-1080: lightweight multi-select for user assignment.
// Used by the task-create dialogs (ClientDetailPage, DealDashboard) so a
// single task may be assigned to multiple users. Mirrors the visual style
// of the existing Select+SelectGroup pattern (company groupings) but emits
// an array of user_ids instead of a single value.
//
// Stabilization-safe: pure UI component — no API calls of its own.
import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { ChevronsUpDown, X } from 'lucide-react';
import { groupUsersByCompany } from '../utils/userGrouping';

export const UserMultiSelect = ({
  users = [],
  companies = [],
  value = [],            // array of user_ids
  onChange,              // (ids: string[]) => void
  placeholder = 'Assign to one or more users…',
  disabled = false,
  testId = 'user-multi-select',
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const groups = useMemo(
    () => groupUsersByCompany(users, { companies }),
    [users, companies],
  );

  const flatById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        users: g.users.filter((u) =>
          (u.full_name || '').toLowerCase().includes(q)
          || (u.email || '').toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.users.length > 0);
  }, [groups, filter]);

  const toggle = (id) => {
    const next = value.includes(id)
      ? value.filter((x) => x !== id)
      : [...value, id];
    onChange?.(next);
  };

  const remove = (id) => onChange?.(value.filter((x) => x !== id));

  const selectedLabel = value.length === 0
    ? placeholder
    : `${value.length} user${value.length === 1 ? '' : 's'} selected`;

  return (
    <div data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between font-normal"
            data-testid={`${testId}-trigger`}
          >
            <span className={value.length === 0 ? 'text-slate-400' : ''}>
              {selectedLabel}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] max-h-72 overflow-hidden"
          align="start"
        >
          <div className="p-2 border-b border-slate-100">
            <Input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search users…"
              className="h-8 text-xs"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">
                No users match.
              </div>
            )}
            {filtered.map((g) => (
              <div key={`${testId}-grp-${g.company_name}`} className="py-1">
                <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  {g.company_name}
                </div>
                {g.users.map((u) => {
                  const checked = value.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      htmlFor={`${testId}-opt-${u.id}`}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                      data-testid={`${testId}-option-${u.id}`}
                    >
                      <Checkbox
                        id={`${testId}-opt-${u.id}`}
                        checked={checked}
                        onCheckedChange={() => toggle(u.id)}
                      />
                      <span className="flex-1">{u.full_name}</span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 mt-2"
          data-testid={`${testId}-selected`}
        >
          {value.map((id) => {
            const u = flatById[id];
            const name = u?.full_name || id;
            return (
              <Badge
                key={id}
                variant="secondary"
                className="text-[11px] font-medium pl-2 pr-1 py-0.5 gap-1"
                data-testid={`${testId}-chip-${id}`}
              >
                {name}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  className="hover:bg-slate-300 rounded-sm p-0.5"
                  aria-label={`Remove ${name}`}
                  data-testid={`${testId}-chip-remove-${id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserMultiSelect;
